import React, { useEffect } from "react";
import { legal } from "@/config/legal";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 border-t border-black/10 pt-10">
      <h2 className="font-heading italic text-3xl md:text-4xl font-semibold tracking-tight mb-4">
        {title}
      </h2>
      <div className="space-y-3 text-sm md:text-base leading-relaxed font-light text-[#333]">
        {children}
      </div>
    </section>
  );
}

export default function LegalPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, []);

  return (
    <div className="bg-[#F4F4F2] text-[#0A0A0A] min-h-screen font-body">
      {/* header */}
      <header className="border-b border-black/10">
        <div className="max-w-3xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" className="font-heading italic text-2xl font-semibold tracking-tighter">
            Wovll
          </a>
          <a
            href="/"
            className="font-mono-accent text-xs uppercase tracking-widest text-[#4F46E5] hover:underline underline-offset-4"
          >
            ← Volver
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <p className="font-mono-accent text-xs uppercase tracking-widest text-[#FF3B30] mb-2">
          Información legal
        </p>
        <h1 className="font-heading italic text-5xl font-semibold tracking-tight mb-8">
          Legal
        </h1>

        {/* índice */}
        <nav className="flex flex-wrap gap-2 mb-10">
          {[
            ["aviso", "Aviso legal"],
            ["privacidad", "Privacidad"],
            ["terminos", "Términos"],
            ["envios", "Envíos"],
            ["devoluciones", "Devoluciones"],
          ].map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              className="px-3 py-1.5 border border-black/15 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="space-y-12">
          <Section id="aviso" title="Aviso legal">
            <p>En cumplimiento de la Ley 34/2002 (LSSI-CE):</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Titular:</strong> {legal.legalName}</li>
              <li><strong>NIF/CIF:</strong> {legal.nif}</li>
              <li><strong>Domicilio:</strong> {legal.address}</li>
              <li><strong>Email:</strong> {legal.email}</li>
              <li><strong>Sitio web:</strong> {legal.domain}</li>
            </ul>
            <p>
              El uso de este sitio atribuye la condición de usuario e implica la aceptación de
              estas condiciones. Todos los contenidos (textos, imágenes, logos, diseño) son
              titularidad de {legal.legalName} o de terceros que han autorizado su uso.
            </p>
          </Section>

          <Section id="privacidad" title="Política de privacidad">
            <p>
              Conforme al RGPD (UE 2016/679) y la LOPDGDD 3/2018, el responsable del tratamiento
              es {legal.legalName} (NIF {legal.nif}), con dirección en {legal.address} y contacto
              en {legal.email}.
            </p>
            <p>
              <strong>Datos que tratamos:</strong> los necesarios para gestionar tu pedido (nombre,
              dirección, email) y el pago. Los datos de la tarjeta los procesa directamente{" "}
              <a href="https://stripe.com/es/privacy" className="text-[#4F46E5] underline" target="_blank" rel="noopener noreferrer">Stripe</a>;
              nosotros no almacenamos tu tarjeta.
            </p>
            <p>
              <strong>Finalidad y base legal:</strong> ejecutar el contrato de compraventa y cumplir
              obligaciones legales (fiscales). <strong>Conservación:</strong> mientras dure la relación
              y los plazos legales exigidos.
            </p>
            <p>
              Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación
              y portabilidad escribiendo a {legal.email}. También puedes reclamar ante la AEPD
              (<a href="https://www.aepd.es" className="text-[#4F46E5] underline" target="_blank" rel="noopener noreferrer">aepd.es</a>).
            </p>
          </Section>

          <Section id="terminos" title="Términos y condiciones de venta">
            <p>Las ventas en {legal.domain} las realiza {legal.legalName} (NIF {legal.nif}).</p>
            <p>
              Los precios se muestran en euros con IVA incluido. El pago se procesa de forma segura
              con Stripe (tarjeta y métodos compatibles). Tras el pago recibirás confirmación por email.
            </p>
            <p>
              <strong>Derecho de desistimiento:</strong> dispones de 14 días naturales para desistir
              de la compra. Además ofrecemos una garantía comercial ampliada de {legal.returnDays} días.
              Todos los productos cuentan con la garantía legal de conformidad prevista en la normativa
              de consumo española.
            </p>
            <p>
              Para reclamaciones puedes acudir a la plataforma europea de resolución de litigios en
              línea:{" "}
              <a href="https://ec.europa.eu/consumers/odr" className="text-[#4F46E5] underline" target="_blank" rel="noopener noreferrer">
                ec.europa.eu/consumers/odr
              </a>.
            </p>
          </Section>

          <Section id="envios" title="Envíos y entregas">
            <ul className="list-disc pl-5 space-y-1">
              <li>Preparamos y enviamos tu pedido tras confirmarse el pago.</li>
              <li>Recibirás un email con el número de seguimiento cuando salga.</li>
              <li>Plazos y costes de envío se muestran en el proceso de compra.</li>
            </ul>
            <p>Ante cualquier incidencia con la entrega, escríbenos a {legal.email}.</p>
          </Section>

          <Section id="devoluciones" title="Devoluciones">
            <p>
              Tienes un plazo legal de <strong>14 días naturales</strong> desde la recepción para
              desistir de tu compra, y una garantía comercial ampliada de{" "}
              <strong>{legal.returnDays} días</strong>.
            </p>
            <p>
              Para devolver un producto, escríbenos a {legal.email} indicando tu número de pedido y
              te enviaremos las instrucciones. Una vez recibido y revisado en buen estado, te
              reembolsamos por el mismo medio de pago en un máximo de 14 días.
            </p>
            <p>
              Si el producto llega defectuoso o no corresponde con lo pedido, lo cambiamos o
              reembolsamos sin coste para ti.
            </p>
          </Section>
        </div>

        <p className="mt-14 rounded bg-black/5 p-4 text-xs text-[#555]">
          ⚠️ Plantilla orientativa. Sustituye los campos «RELLENAR» (en{" "}
          <code>src/config/legal.js</code>) por tus datos reales y revísala con un asesor legal antes
          de publicar. No constituye asesoramiento jurídico.
        </p>
      </main>

      <footer className="border-t border-black/10 py-8">
        <div className="max-w-3xl mx-auto px-6 font-mono-accent text-[10px] uppercase tracking-widest text-[#777]">
          © {new Date().getFullYear()} {legal.brand}
        </div>
      </footer>
    </div>
  );
}
